import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { RequireAuth } from './RequireAuth';
import { LoginBrand } from './auth/LoginBrand';
import { VerifyingOverlay } from './ui/VerifyingOverlay';
import { CalendarAppShell } from './layout/CalendarAppShell';
import { useAuth } from '../contexts/AuthContext';
import { DEFAULT_CALENDAR_SLUG, isCalendarAllowlisted } from '../lib/calendarWorkspace';

function CalendarAllowlistGate({ children }: { children: React.ReactNode }) {
  const { session, signOut } = useAuth();
  const email = session?.user?.email ?? '';
  const [ok, setOk] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const allowed = await isCalendarAllowlisted(email);
        if (!cancelled) setOk(allowed);
      } catch (e) {
        if (!cancelled) {
          setOk(false);
          setError(e instanceof Error ? e.message : 'Could not verify calendar access.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [email]);

  if (ok === null) {
    return (
      <div className="login-page">
        <div className="login-card">
          <LoginBrand />
          <div className="login-card__body">
            <p className="login-card__lead">Calendar workspace</p>
          </div>
        </div>
        <VerifyingOverlay message="Checking calendar access…" />
      </div>
    );
  }

  if (!ok) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h1>Calendar access required</h1>
          <p className="detail-text">
            {error ??
              'Your account is not on the calendar allowlist. Ask the workspace owner to invite you.'}
          </p>
          <div className="login-page__actions">
            <button type="button" className="btn-secondary" onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return children;
}

export function CalendarWorkspaceApp() {
  const { slug } = useParams<{ slug: string }>();
  if (!slug || slug !== DEFAULT_CALENDAR_SLUG) {
    return <Navigate to={`/calendar/${DEFAULT_CALENDAR_SLUG}`} replace />;
  }

  return (
    <RequireAuth>
      <CalendarAllowlistGate>
        <CalendarAppShell slug={slug} />
      </CalendarAllowlistGate>
    </RequireAuth>
  );
}
